import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Sparkles, Menu, X } from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { useT } from "../../context/LanguageContext";
import { Button } from "../ui/button";
import { LanguageSwitcher } from "./LanguageSwitcher";
import api from "../../lib/api";

export const Navbar = () => {
  const { user, logout, setUser } = useAuth();
  const { t, lang } = useT();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);

  // Persist the owner's app language to their account so server-sent emails
  // (e.g. the Weekly Performance Email) use the same language. No-op when
  // already in sync; failures are silent (UI language still works locally).
  React.useEffect(() => {
    if (!user) return;
    const saved = user.language || "en";
    if (lang !== saved) {
      api
        .patch("/auth/me", { language: lang })
        .then(({ data }) => setUser && setUser(data))
        .catch(() => {});
    }
  }, [lang, user, setUser]);

  const onDashboard =
    location.pathname.startsWith("/dashboard") ||
    location.pathname.startsWith("/leads/") ||
    location.pathname.startsWith("/calendar") ||
    location.pathname.startsWith("/settings") ||
    location.pathname.startsWith("/financials") ||
    location.pathname.startsWith("/growth-ai") ||
    location.pathname.startsWith("/admin");

  return (
    <header
      data-sticky-header="true"
      className="bg-white/80 backdrop-blur-xl border-b border-gray-200 sticky top-0 z-40"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          <Link to="/" className="flex items-center gap-2" data-testid="navbar-logo">
            <span className="w-8 h-8 rounded-lg bg-green-800 text-white flex items-center justify-center">
              <Sparkles className="w-4 h-4" />
            </span>
            <span className="font-display font-bold text-lg tracking-tight">Tasklio AI</span>
          </Link>

          <nav className="hidden lg:flex items-center gap-8">
            {onDashboard && user && (
              <>
                <Link to="/dashboard" className="text-sm font-semibold text-gray-700 hover:text-gray-900 transition" data-testid="nav-dashboard">{t("nav.dashboard")}</Link>
                <Link to="/calendar" className="text-sm font-semibold text-gray-700 hover:text-gray-900 transition" data-testid="nav-calendar">{t("nav.calendar")}</Link>
                <Link to="/financials" className="text-sm font-semibold text-gray-700 hover:text-gray-900 transition" data-testid="nav-financials">{t("nav.financials")}</Link>
                <Link to="/growth-ai" className="text-sm font-semibold text-gray-700 hover:text-gray-900 transition" data-testid="nav-growth-ai">{t("nav.growthAi")}</Link>
                <Link to="/settings" className="text-sm font-semibold text-gray-700 hover:text-gray-900 transition" data-testid="nav-settings">{t("nav.settings")}</Link>
                {user.is_admin ? (
                  <Link to="/admin" className="text-sm font-semibold text-amber-800 hover:text-amber-900 transition" data-testid="nav-admin">{t("nav.admin")}</Link>
                ) : null}
              </>
            )}
          </nav>

          <div className="hidden lg:flex items-center gap-4">
            {user ? (
              <>
                {!onDashboard && (
                  <>
                    <Link to="/pricing" className="text-sm font-semibold text-gray-700 hover:text-gray-900 transition" data-testid="nav-pricing">{t("nav.pricing")}</Link>
                    <Button
                      onClick={() => navigate("/dashboard")}
                      className="bg-green-800 hover:bg-green-900 text-white font-semibold shadow-sm"
                      data-testid="nav-go-dashboard"
                    >
                      {t("nav.dashboard")}
                    </Button>
                  </>
                )}
                {onDashboard && (
                  <>
                    <LanguageSwitcher testidPrefix="navbar-lang-switch" />
                    <span className="text-sm text-gray-500" data-testid="nav-user-email">{user.email}</span>
                    <Button
                      variant="outline"
                      onClick={() => { logout(); navigate("/"); }}
                      data-testid="nav-logout"
                    >
                      {t("nav.logout")}
                    </Button>
                  </>
                )}
              </>
            ) : (
              <>
                <Link to="/find-a-cleaner" className="text-sm font-semibold text-gray-700 hover:text-gray-900 transition" data-testid="nav-find-cleaner">
                  {t("nav.findCleaner")}
                </Link>
                <Link to="/pricing" className="text-sm font-semibold text-gray-700 hover:text-gray-900 transition" data-testid="nav-pricing">
                  {t("nav.pricing")}
                </Link>
                <Link to="/login" className="text-sm font-semibold text-gray-700 hover:text-gray-900 transition" data-testid="nav-login">
                  {t("nav.login")}
                </Link>
                <LanguageSwitcher testidPrefix="navbar-lang-switch" />
                <Button
                  onClick={() => navigate("/register")}
                  className="bg-green-800 hover:bg-green-900 text-white font-semibold shadow-sm px-5"
                  data-testid="nav-signup"
                >
                  {t("nav.startTrial")}
                </Button>
              </>
            )}
          </div>

          <button
            className="lg:hidden p-2"
            onClick={() => setOpen((v) => !v)}
            data-testid="nav-mobile-toggle"
          >
            {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden border-t border-gray-200 bg-white px-4 py-4 space-y-3">
          {user ? (
            <>
              <Link to="/dashboard" className="block text-base font-semibold text-gray-800" onClick={() => setOpen(false)} data-testid="nav-mobile-dashboard">{t("nav.dashboard")}</Link>
              <Link to="/calendar" className="block text-base font-semibold text-gray-800" onClick={() => setOpen(false)} data-testid="nav-mobile-calendar">{t("nav.calendar")}</Link>
              {/* Marketing Pricing link kept for logged-in users who are
                  NOT on the dashboard (e.g. browsing the landing page).
                  When the user is on the dashboard the standalone
                  Billing entry is removed — billing now lives inside
                  Settings → Billing & Subscription. */}
              {!onDashboard && (
                <Link to="/pricing" className="block text-base font-semibold text-gray-800" onClick={() => setOpen(false)} data-testid="nav-mobile-pricing">{t("nav.pricing")}</Link>
              )}
              <Link to="/financials" className="block text-base font-semibold text-gray-800" onClick={() => setOpen(false)} data-testid="nav-mobile-financials">{t("nav.financials")}</Link>
              <Link to="/growth-ai" className="block text-base font-semibold text-gray-800" onClick={() => setOpen(false)} data-testid="nav-mobile-growth-ai">{t("nav.growthAi")}</Link>
              <Link to="/settings" className="block text-base font-semibold text-gray-800" onClick={() => setOpen(false)} data-testid="nav-mobile-settings">{t("nav.settings")}</Link>
              {user.is_admin ? (
                <Link to="/admin" className="block text-base font-semibold text-amber-800" onClick={() => setOpen(false)} data-testid="nav-mobile-admin">{t("nav.admin")}</Link>
              ) : null}
              <div className="pt-2">
                <LanguageSwitcher testidPrefix="navbar-mobile-lang-switch" />
              </div>
              <button onClick={() => { logout(); navigate("/"); setOpen(false); }} className="block text-base font-semibold text-red-600" data-testid="nav-mobile-logout">{t("nav.logout")}</button>
            </>
          ) : (
            <>
              <Link to="/find-a-cleaner" className="block text-base font-semibold text-gray-800" onClick={() => setOpen(false)} data-testid="nav-mobile-find-cleaner">{t("nav.findCleaner")}</Link>
              <Link to="/pricing" className="block text-base font-semibold text-gray-800" onClick={() => setOpen(false)} data-testid="nav-mobile-pricing">{t("nav.pricing")}</Link>
              <Link to="/login" className="block text-base font-semibold text-gray-800" onClick={() => setOpen(false)} data-testid="nav-mobile-login">{t("nav.login")}</Link>
              <div className="pt-1">
                <LanguageSwitcher testidPrefix="navbar-mobile-lang-switch" />
              </div>
              <Link to="/register" className="block w-full text-center bg-green-800 hover:bg-green-900 text-white font-semibold rounded-lg px-4 py-2.5" onClick={() => setOpen(false)} data-testid="nav-mobile-signup">{t("nav.startTrial")}</Link>
            </>
          )}
        </div>
      )}
    </header>
  );
};
